<template>
  <Head :title="props.article.title">
    <meta v-if="metaData.description" name="description" :content="metaData.description" />
    <meta v-if="metaData.keywords" name="keywords" :content="metaData.keywords" />
    <meta v-if="metaData.ogImage" property="og:image" :content="metaData.ogImage" />
    <link v-if="metaData.canonical_link" rel="canonical" :href="metaData.canonical_link" />
    <meta v-if="metaData.author" name="author" :content="metaData.author" />
    <meta
      v-if="metaData.publishedAt"
      property="article:published_time"
      :content="metaData.publishedAt" />
    <meta
      v-if="metaData.modifiedAt"
      property="article:modified_time"
      :content="metaData.modifiedAt" />
  </Head>
  <BlogLayout>
    <IContainer>
      <IRow>
        <IColumn>
          <div class="_text:left _margin-top:4 _margin-bottom:2 pt-8">
            <Link
              class="_display:flex _text:primary _text:normal _padding-right:1"
              :href="route('blog.index')"
              @click="redirectBack"
              title="Go Back to Blog Posts">
              <ChevronLeftIcon
                class="_display:flex _align-self:center _margin-right:1/2 action-icon" />
              Back to Blog Posts
            </Link>
          </div>
        </IColumn>
      </IRow>
    </IContainer>
    <div class="mb-10">
      <IContainer>
        <IRow>
          <IColumn xs="12">
            <ICard color="light">
              <IMedia v-if="article.featured_image">
                <template #image>
                  <img
                    class="_display:flex _flex:fill _margin:0"
                    :src="article.featured_image"
                    :alt="article.featured_image_caption || article.title" />
                </template>
              </IMedia>
              <div class="_display:flex _xs:flex-direction:column _sm:flex-direction:row">
                <span
                  class="_display:flex _flex:fill _xs:padding-bottom:0 _xs:padding-top:1/2 _xs:padding-x:1 _sm:padding-y:1 _border-bottom _border-color:light _background:light-25 _text:dark-50 _text:normal _text:md">
                  {{ castDateFull(article.published_at) }}
                </span>
                <span
                  class="_xs:padding-top:0 _xs:padding-bottom:1/2 _sm:padding-y:1 _xs:padding-x:1 _border-bottom _border-color:light _background:light-25 _text:dark-50 _text:md _text:normal">
                  {{ article.read_time }}
                </span>
              </div>
              <div class="_padding:2">
                <h1
                  class="d6 _xs:hidden _md:visible _text:dark-45 _text:normal _margin-bottom:1/2 _margin-top:1/2">
                  {{ article.title }}
                </h1>
                <h1
                  class="_xs:visible _md:hidden _white-space:normal _text:dark-45 _text:normal _margin-bottom:1/2 _margin-top:1/2">
                  {{ article.title }}
                </h1>

                <div class="_margin-bottom:1">
                  <span class="_text:lg _text:dark-30">
                    Written by:
                    <span class="_text:dark-40">{{ article.user.name }}</span>
                  </span>
                </div>

                <div
                  class="_text:dark-30 prose prose-lg max-w-none prose-blockquote:text-gray-500 prose-blockquote:border-red-500 prose-blockquote:font-light _text:lg _margin-bottom:2"
                  v-html="article.body" />

                <div class="_display:flex _flex-wrap:wrap _gap:1">
                  <IBadge
                    v-for="tag in article.tags"
                    :key="tag.id"
                    class="_text:dark-30 _margin-right:2/3 _text:normal"
                    color="light">
                    {{ tag.name }}
                  </IBadge>
                </div>
              </div>
            </ICard>
          </IColumn>
        </IRow>
      </IContainer>
    </div>
  </BlogLayout>
</template>

<script setup>
  import { computed, onMounted } from 'vue'
  import { Head, Link } from '@inertiajs/vue3'
  import BlogLayout from '@/Layouts/BlogLayout.vue'
  import { castDateFull } from '@/Services/castDateFull.js'
  import { ChevronLeftIcon } from '@heroicons/vue/24/outline'
  import { useMeta } from '@/Composables/useMeta'

  const props = defineProps({
    article: {
      type: Object,
      required: true,
    },
  })

  // Create computed properties for meta data
  const metaData = computed(() => ({
    description:
      props.article.description || props.article.summary || `${props.article.title} - Blog Article`,
    keywords: props.article.tags?.map((tag) => tag.name)?.join(', ') || '',
    ogImage: props.article.featured_image || '',
    author: props.article.user.name,
    publishedAt: props.article.published_at,
    modifiedAt: props.article.updated_at,
    canonical_link: props.article.canonical_url || route('blog.show', props.article.slug),
    og_title: props.article.title,
    og_description: props.article.description || props.article.summary,
    og_type: 'article',
    og_url: route('blog.show', props.article.slug),
  }))

  const { setMeta } = useMeta()

  // Set meta tags on component mount
  onMounted(() => {
    setMeta({
      ...metaData.value,
    })
  })

  const redirectBack = () => {
    window.history.back()
  }
</script>

<style scoped>
  .action-icon {
    width: 20px;
    height: 20px;
    stroke-width: 0.1em;
  }
</style>

<style>
  blockquote {
    font-size: 2rem;
    font-weight: 600;
    margin: 2em 0.5em;
  }
</style>
